import * as React from 'react'

import { stripSectionAudioFromHeadingText } from '@/utils/audio-phrase-markdown'
import { createHeadingId } from '@/utils/heading-id-generate'

import MarkdownPreview from './markdown-preview'

interface TableOfContentsProps {
  content: string
  className?: string
  theme?: 'light' | 'dark'
}

function createManualToc(content: string): string {
  const lines = content.split('\n')
  const tocItems: string[] = []

  let insideCodeBlock = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    if (line.trim().startsWith('```')) {
      insideCodeBlock = !insideCodeBlock
      continue
    }

    if (insideCodeBlock) continue

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)

    if (headingMatch) {
      const level = headingMatch[1].length
      const text = stripSectionAudioFromHeadingText(headingMatch[2])

      if (!text || level > 3) continue

      const id = createHeadingId(text)

      const indent = '  '.repeat(level - 1)

      let headingIcon = ''
      if (level === 1) {
        headingIcon = '📌'
      } else if (level === 2) {
        headingIcon = '📎'
      } else if (level === 3) {
        headingIcon = '🔹'
      }

      const headingPrefix = headingIcon ? `<span class="heading-icon">${headingIcon}</span> ` : ''

      tocItems.push(`${indent}- [${headingPrefix}${text}](#${id})`)
    }
  }

  return tocItems.join('\n')
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ content, className = '', theme = 'light' }) => {
  const [activeId, setActiveId] = React.useState<string>('')

  React.useEffect(() => {
    const handleTocLinkClick = (e: Event) => {
      const mouseEvent = e as MouseEvent
      const target = mouseEvent.target as HTMLElement

      // Check if it's a TOC link
      if (target.tagName === 'A' && target.closest('.toc-container')) {
        mouseEvent.preventDefault()

        const href = target.getAttribute('href')
        if (href && href.startsWith('#')) {
          const targetId = href.substring(1)
          const targetElement = document.getElementById(targetId)

          if (targetElement) {
            // Calculate position to center the heading in the viewport
            const headerOffset = 100 // Adjust based on your header height
            const elementPosition = targetElement.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            })

            targetElement.classList.add('heading-highlight')
            setTimeout(() => {
              targetElement.classList.remove('heading-highlight')
            }, 2000) // Remove highlight after 2 seconds

            setActiveId(targetId)

            // Update URL hash without scrolling
            window.history.pushState(null, '', href)
          }
        }
      }
    }

    const tocContainer = document.querySelector('.toc-container')
    if (tocContainer) {
      tocContainer.addEventListener('click', handleTocLinkClick)
    }

    return () => {
      if (tocContainer) {
        tocContainer.removeEventListener('click', handleTocLinkClick)
      }
    }
  }, [])

  React.useEffect(() => {
    // Function to check which section is currently in view
    const handleScroll = () => {
      const headings = Array.from(document.querySelectorAll('h2, h3, h4'))

      // Find the heading that's currently at the top of the viewport
      const currentHeading = headings.find(heading => {
        const rect = heading.getBoundingClientRect()
        return rect.top >= 0 && rect.top <= 200
      })

      if (currentHeading) {
        setActiveId(currentHeading.id)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    // Initial check
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Add active class to the current section in the TOC
  React.useEffect(() => {
    if (!activeId) return

    const tocLinks = document.querySelectorAll('.toc-container a')
    tocLinks.forEach(link => {
      const href = link.getAttribute('href')
      if (href && href === `#${activeId}`) {
        link.classList.add('active')
      } else {
        link.classList.remove('active')
      }
    })
  }, [activeId])

  // Create a manual TOC from the content
  const manualToc = createManualToc(content)

  const isDark = theme === 'dark'
  const containerClass = isDark
    ? 'toc-container prose prose-sm prose-invert max-w-none [&_a]:text-zinc-200 [&_a:hover]:text-white [&_a]:underline-offset-2 [&_.active]:text-blue-400 [&_.active]:font-medium'
    : 'toc-container prose prose-sm max-w-none'

  return (
    <div className={`${containerClass} ${className}`}>
      <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-zinc-50' : ''}`}>Table of Contents</h3>
      <div className="toc-content custom-scrollbar">
        <MarkdownPreview>{manualToc}</MarkdownPreview>
      </div>
    </div>
  )
}

export default TableOfContents
